import {
  type SanityCombinedListingConfiguration,
  type SanityCombinedListingProductFragment,
  type SanityCombinedListingProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { compareNumbers } from '@lib/helpers'
import { type Parameter } from '@lib/parameters'
import { sizeOptionNames } from '@lib/product/product'
import { getValidSizeOptionValues } from './size'
import {
  type CombinedListing,
  type CombinedListingProductResult,
  type CombinedListingProductType,
} from './types'

export const shopifyCombinedListingProductType = 'Combined Listing'

const combinedListingProductTypes: CombinedListingProductType[] = [
  'frame',
  'glass',
  'backboard',
  'passepartout',
  'spacer',
]

const combinedListingProductTypeWeights: Record<
  CombinedListingProductType,
  number
> = {
  frame: 1,
  glass: 2,
  backboard: 3,
  passepartout: 4,
  spacer: 5,
}

/**
 * Groups combined listing sub-products by size.
 */
export const getCombinedListingProductsBySize = (
  frameProducts: SanityCombinedListingProductFragment[],
  glassProducts: SanityCombinedListingProductFragment[],
  backboardProducts: SanityCombinedListingProductFragment[],
  passepartoutProducts: SanityCombinedListingProductFragment[],
  spacerProducts: SanityCombinedListingProductFragment[],
) => {
  const combinedListing: CombinedListing = {}

  const addProduct = (
    type: CombinedListingProductType,
    product: SanityCombinedListingProductFragment,
  ) => {
    const sizes = getValidSizeOptionValues(product.options)

    sizes.forEach((size) => {
      // Take sizes only from frame products
      if (!combinedListing[size] && type === 'frame') {
        combinedListing[size] = {
          frame: [],
          glass: [],
          backboard: [],
          passepartout: [],
          spacer: [],
        }
      }

      if (combinedListing[size]) {
        combinedListing[size][type].push(product)
      }
    })
  }

  // Add all products to the combined listing configuration starting with frame products
  frameProducts.forEach((product) => addProduct('frame', product))
  glassProducts.forEach((product) => addProduct('glass', product))
  backboardProducts.forEach((product) => addProduct('backboard', product))
  passepartoutProducts.forEach((product) => addProduct('passepartout', product))
  spacerProducts.forEach((product) => addProduct('spacer', product))

  return combinedListing
}

/**
 * Finds a product and its type in combined listing configuration by its ID.
 */
export const findCombinedListingProduct = (
  combinedListingConfiguration: SanityCombinedListingConfiguration,
  productId: number,
) =>
  combinedListingProductTypes.reduce<CombinedListingProductResult | null>(
    (result, type) => {
      if (result !== null) {
        return result
      }

      const product = combinedListingConfiguration[type]?.find(
        (product) => product.productID === productId,
      )

      if (!product) {
        return result
      }

      return {
        type,
        product,
      }
    },
    null,
  )

/**
 * Finds products from combined listing configuration and product IDs.
 */
export const findCombinedListingProducts = (
  combinedListingConfiguration: SanityCombinedListingConfiguration,
  productIds: number[],
) => {
  const combinedListingProductResults = productIds
    .map((productId) => {
      return findCombinedListingProduct(combinedListingConfiguration, productId)
    })
    .filter(Boolean) as CombinedListingProductResult[]

  const sortedCombinedListingProductResults =
    combinedListingProductResults.sort((result1, result2) => {
      return compareNumbers(
        combinedListingProductTypeWeights[result1.type],
        combinedListingProductTypeWeights[result2.type],
      )
    })

  return sortedCombinedListingProductResults.map((result) => result.product)
}

/**
 * Gets combined listing product variants from products and frame size.
 */
export const getCombinedListingVariants = (
  products: SanityCombinedListingProductFragment[],
  frameSize: string | null,
) => {
  if (!frameSize) {
    return []
  }

  return products
    .map((product) =>
      product.variants?.find((variant) => {
        const sizeOption = variant.options.find((option) =>
          sizeOptionNames.includes(option.name),
        )

        return sizeOption?.value === frameSize
      }),
    )
    .filter(Boolean) as SanityCombinedListingProductVariantFragment[]
}

/**
 * Gets combined listing product IDs from parameters.
 */
export const getCombinedListingProductsIds = (parameters: Parameter[]) => {
  const productsParameter = parameters.find(
    (parameter) => parameter.name === 'products',
  )
  const productsIds = productsParameter?.value
    ?.split(',')
    ?.filter(Boolean)
    ?.map((productsId) => Number(productsId))

  return productsIds ?? []
}

/**
 * Gets selected product IDs by type from parameters.
 */
export const getCombinedListingProductSelection = (
  combinedListingConfiguration: SanityCombinedListingConfiguration,
  parameters: Parameter[],
) => {
  const productSelection: Record<CombinedListingProductType, number | null> = {
    frame: null,
    glass: null,
    backboard: null,
    passepartout: null,
    spacer: null,
  }

  // Get existing product selection
  const productsIds = getCombinedListingProductsIds(parameters)
  productsIds.forEach((activeProductsId) => {
    const result = findCombinedListingProduct(
      combinedListingConfiguration,
      activeProductsId,
    )

    if (result) {
      productSelection[result.type] = result.product.productID
    }
  })

  return productSelection
}
